import { cardAbilityIsActive } from '../cards/abilities';
import {
  cardSuperCannonBodies,
  cardSuperCannonLateralMu,
  cardSuperCannonMuzzles,
} from '../cards/geometry';
import { pvpHullRadiusMu } from './combat-profile';
import type { PvpParticipantShipState } from './roster';

/**
 * **Os dois canhões temporários dentro de um duelo** (gate V28H.J).
 *
 * ## Porque a geometria vive aqui e não na simulação
 *
 * Porque ela é a mesma pergunta feita em dois sítios: a sala precisa dela para
 * saber de onde saem os tiros, e a cena precisa dela para saber onde desenhar os
 * dois canhões. Escrita uma vez e pura, as duas respostas não podem divergir - e
 * é essa divergência que faria um jogador levar tiros de uma arma desenhada
 * noutro sítio.
 *
 * ## Porque o afastamento é uma PROPORÇÃO
 *
 * As duas arenas têm unidades e cascos diferentes, e o que o dono aprovou não
 * foi um número de pixels - foi a relação: os canhões ficam um pouco para lá da
 * meia-largura da nave. A proporção vive em `cards/abilities.ts`, que é neutra
 * entre os modos, e cada lado converte-a para as suas unidades.
 *
 * Um afastamento absoluto pareceria colado a uma nave pequena e perdido ao lado
 * de uma grande.
 */

/** A carta que monta os dois canhões. A única deste gate. */
export const PVP_SUPER_CANNONS_CARD = 'card_super_cannons';

/** Se este casco tem os dois canhões montados NESTE instante. */
export function shipHasSuperCannons(ship: PvpParticipantShipState): boolean {
  return ship.cards.some(
    (card) => card.definitionId === PVP_SUPER_CANNONS_CARD && cardAbilityIsActive(card),
  );
}

/**
 * O afastamento lateral de um canhão, em milli-unidades desta arena.
 *
 * Proporcional ao raio do casco, e não um número absoluto: a mesma proporção que
 * a caça desenha - o canhão fica um pouco para lá da meia-largura do casco -
 * traduzida para a nave que este duelo tem.
 */
export function pvpSuperCannonLateralMu(ship: PvpParticipantShipState): number {
  return cardSuperCannonLateralMu(pvpHullRadiusMu(ship.profile));
}

/**
 * As duas BOCAS, em milli-unidades da arena, na ordem esquerda e direita.
 *
 * Perpendiculares à mira: o vetor da mira roda com o casco, portanto os dois
 * canhões acompanham-no sem que nada aqui saiba o que é um ângulo. O tiro sai da
 * frente do canhão, à mesma distância a que o tiro do casco sai da frente do
 * casco - senão um canhão dispararia de dentro de si mesmo.
 *
 * Vazia quando os canhões não estão montados: quem chama desenha exatamente o
 * que isto devolve.
 */
export function pvpSuperCannonMuzzles(
  ship: PvpParticipantShipState,
): readonly { readonly x: number; readonly y: number }[] {
  /*
   * A aritmética saiu daqui para `cards/geometry.ts` no gate V28I.P, sem mudar
   * um número: o mundo aberto monta os mesmos dois canhões, e uma terceira
   * cópia da mesma conta seria a terceira oportunidade de eles divergirem. A
   * condição de "montados" fica, porque essa é do duelo.
   */
  return shipHasSuperCannons(ship)
    ? cardSuperCannonMuzzles(ship, pvpHullRadiusMu(ship.profile))
    : [];
}

/**
 * Os dois CORPOS, para a cena os desenhar.
 *
 * Mais atrás do que as bocas, pela mesma razão que a caça os põe atrás da
 * formação: o corpo do canhão fica ao lado do casco e a boca projecta-se à
 * frente dele. Uma cena que desenhasse o corpo na boca poria a arte meio corpo
 * à frente de onde ela devia estar.
 */
export function pvpSuperCannonBodies(
  ship: PvpParticipantShipState,
): readonly { readonly x: number; readonly y: number }[] {
  return shipHasSuperCannons(ship)
    ? cardSuperCannonBodies(ship, pvpHullRadiusMu(ship.profile))
    : [];
}
